import { Node, mergeAttributes, InputRule } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { ReactNodeViewRenderer } from '@tiptap/react';
import VariableNodeView from './VariableNodeView';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    variable: {
      insertVariable: (attrs: { tag: string; label: string }) => ReturnType;
      setVariablesMap: (map: Record<string, string>) => ReturnType;
    };
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getVariablesMap(editor: any): Record<string, string> {
  return editor?.storage?.variable?.variablesMap ?? {};
}

/**
 * Inline atom node for HubSpot variables like {{deal.name}}.
 * Renders as a highlighted chip that shows the resolved value or the tag as fallback.
 *
 * The resolved values are stored in `editor.storage.variable.variablesMap`
 * and kept in sync via the `setVariablesMap` command.
 */
export const VariableNode = Node.create({
  name: 'variable',
  group: 'inline',
  inline: true,
  atom: true,

  addStorage() {
    return {
      variablesMap: {} as Record<string, string>,
    };
  },

  addAttributes() {
    return {
      tag: { default: '' },
      label: { default: '' },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-variable]' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-variable': node.attrs.tag,
        class: 'variable-node',
      }),
      node.attrs.label || node.attrs.tag,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(VariableNodeView) as any;
  },

  addCommands() {
    return {
      insertVariable:
        (attrs) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs,
          });
        },
      setVariablesMap:
        (map) =>
        ({ editor, tr }) => {
          (editor.storage as any).variable.variablesMap = map;
          tr.setMeta('variablesMapUpdated', true);
          tr.setMeta('__variablesMap', map);
          return true;
        },
    };
  },

  addInputRules() {
    const nodeType = this.type;
    return [
      new InputRule({
        find: /\{\{([a-zA-Z_.]+)\}\}$/,
        handler: ({ state, range, match }) => {
          const tag = `{{${match[1]}}}`;
          const label = match[1];
          const { tr } = state;
          tr.replaceWith(range.from, range.to, nodeType.create({ tag, label }));
        },
      }),
    ];
  },

  addProseMirrorPlugins() {
    const variableDecoPluginKey = new PluginKey('variableDecorations');
    let currentMap: Record<string, string> = {};

    function buildDecorations(doc: any, map: Record<string, string>): DecorationSet {
      const decorations: Decoration[] = [];
      const pattern = /\{\{([a-zA-Z_.]+)\}\}/g;

      doc.descendants((node: any, pos: number) => {
        if (!node.isText || !node.text) return;
        let match;
        pattern.lastIndex = 0;
        while ((match = pattern.exec(node.text)) !== null) {
          const from = pos + match.index;
          const to = from + match[0].length;
          const tag = match[0];
          const resolved = map[tag] || '';

          if (resolved) {
            const widget = document.createElement('span');
            widget.className = 'variable-resolved';
            widget.title = tag;
            widget.textContent = resolved;
            widget.style.cssText = 'font:inherit;color:inherit;';
            // ProseMirror hergebruikt een widget met dezelfde key: met de waarde erin verschijnt
            // een gewijzigde waarde (HubSpot vernieuwd, ander totaal) ook echt in de tekst.
            decorations.push(Decoration.widget(from, widget, { side: 0, key: `${tag}@${pos}=${resolved}` }));
            decorations.push(Decoration.inline(from, to, {
              style: 'font-size:0;line-height:0;overflow:hidden;width:0;display:inline-block;',
            }));
          } else {
            decorations.push(Decoration.inline(from, to, {
              class: 'variable-chip',
              style: 'background:rgba(0,7,236,0.08);color:rgb(0,7,236);border:1px solid rgba(0,7,236,0.2);border-radius:4px;padding:0 4px;font-size:0.75rem;font-weight:500;',
            }));
          }
        }
      });

      return DecorationSet.create(doc, decorations);
    }

    return [
      new Plugin({
        key: variableDecoPluginKey,
        state: {
          init(_, state) {
            return buildDecorations(state.doc, currentMap);
          },
          apply(tr, oldSet) {
            const metaMap = tr.getMeta('__variablesMap') as Record<string, string> | undefined;
            if (metaMap) {
              currentMap = metaMap;
            }
            if (tr.docChanged || tr.getMeta('variablesMapUpdated')) {
              return buildDecorations(tr.doc, currentMap);
            }
            return oldSet.map(tr.mapping, tr.doc);
          },
        },
        props: {
          decorations(state) {
            return variableDecoPluginKey.getState(state);
          },
        },
      }),
    ];
  },
});
